import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Topbar from '../components/Topbar';
import axios from '../libs/axios';

export default function Jobs({ token }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchJobs = async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/jobs');
      setJobs(res.data.jobs || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchJobs(); /* eslint-disable-next-line */ }, [token]);

  // Polling: every ~50 minutes, refresh jobs and check status for pending jobs
  useEffect(() => {
    if (!token) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get('/jobs');
        const list = res.data.jobs || [];
        setJobs(list);
        // For jobs pending at DATEV, trigger status check
        const pending = list.filter((j) => ['PENDING_AT_DATEV', 'UPLOADING', 'SENT_TO_DATEV'].includes(j.status));
        if (pending.length > 0) {
          await Promise.allSettled(pending.map((j) => axios.get(`/jobs/${j.id}/datev/status`)));
          // Refresh after status checks
          const refreshed = await axios.get('/jobs');
          setJobs(refreshed.data.jobs || []);
        }
      } catch (_e) {
        // ignore polling errors
      }
    }, 3000000); // 50 minutes
    return () => clearInterval(interval);
  }, [token]);

  const process = async (id) => {
    try {
      const res = await axios.post(`/jobs/${id}/process`);
      if (res.status !== 200) throw new Error('Failed to start processing');
      await fetchJobs();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  const uploadToDatev = async (id) => {
    try {
      const res = await axios.post(`/jobs/${id}/datev/upload`);
      if (res.status !== 200) throw new Error('Failed to upload to DATEV');
      await fetchJobs();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  const checkStatus = async (id) => {
    try {
      const res = await axios.get(`/jobs/${id}/datev/status`);
      if (res.status !== 200) throw new Error('Failed to check status');
      await fetchJobs();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  if (!token) {
    return (
      <div className="landing">
        <div className="landing-inner">
          <h1>Jobs</h1>
          <p>Please sign in to view your jobs.</p>
          <div className="actions">
            <Link className="btn-primary" to="/login">Login</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <Topbar />
      <main className="content">
        <h2>Jobs</h2>
        {error && <div className="alert error">{error}</div>}
        {loading ? (
          <div className="muted">Loading...</div>
        ) : jobs.length === 0 ? (
          <div className="muted">No jobs yet. Create one.</div>
        ) : (
          <div className="card">
            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr style={{ textAlign: 'left' }}>
                    <th style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>Name</th>
                    <th style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>Status</th>
                    <th style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>Input CSV</th>
                    <th style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>EXTF</th>
                    <th style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>PDFs</th>
                    <th style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>Progress</th>
                    <th style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>Last status</th>
                    <th style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}>Created</th>
                    <th style={{ padding: 8, borderBottom: '1px solid #e5e7eb' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((j) => (
                    <tr key={j.id}>
                      <td style={{ padding: 8, borderBottom: '1px solid #f1f5f9' }}>
                          <Link to={`/jobs/${j.id}`} className='link'>
                              <div className='strong'>{j.name || 'Untitled'}</div>
                              <div
                                  className='muted'
                                  style={{fontSize: 12}}
                              >
                                  {j.description || ''}
                              </div>
                          </Link>
                      </td>
                      <td style={{ padding: 8, borderBottom: '1px solid #f1f5f9' }}>{j.status}</td>
                      <td style={{ padding: 8, borderBottom: '1px solid #f1f5f9' }}>
                        {j.csvFileName ? (
                          <a href={j.csvFilePath} target="_blank" rel="noreferrer" className="link">{j.csvFileName}</a>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td style={{ padding: 8, borderBottom: '1px solid #f1f5f9' }}>
                        {j.csvExtfUrl ? (
                          <a href={j.csvExtfUrl} target="_blank" rel="noreferrer" className="link">EXTF</a>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td style={{ padding: 8, borderBottom: '1px solid #f1f5f9' }}>
                        {Array.isArray(j.pdfUrls) ? j.pdfUrls.length : 0}
                      </td>
                      <td style={{ padding: 8, borderBottom: '1px solid #f1f5f9' }}>
                        {typeof j.uploadedCount === 'number' && typeof j.totalCount === 'number' ? `${j.uploadedCount}/${j.totalCount}` : '—'}
                      </td>
                      <td style={{ padding: 8, borderBottom: '1px solid #f1f5f9' }}>
                        <div>{j.datevStatus || '—'}</div>
                        <div className="muted" style={{ fontSize: 12 }}>
                          {j.lastStatusCheck ? new Date(j.lastStatusCheck).toLocaleString() : ''}
                        </div>
                      </td>
                      <td style={{ padding: 8, borderBottom: '1px solid #f1f5f9' }}>{new Date(j.createdAt || Date.now()).toLocaleString()}</td>
                      <td style={{ padding: 8, borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 8 }}>
                        {!j.csvExtfUrl && j.status === 'READY' && (
                          <button className="btn-primary" onClick={() => process(j.id)}>Process</button>
                        )}
                        {j.csvExtfUrl && j.status === 'READY' && (
                          <button className="btn-primary" onClick={() => uploadToDatev(j.id)}>Upload to DATEV</button>
                        )}
                        {(['PENDING_AT_DATEV', 'UPLOADING', 'SENT_TO_DATEV'].includes(j.status)) && (
                          <button className="btn-secondary" onClick={() => checkStatus(j.id)}>Check status</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
